import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import auth from './AuthService';
import './EventsList.css'; // Import new CSS file

const AppointmentRow = ({ appt, eventId, onEdit, onDelete, onQueue, onStop, onRemoveMe, isAdmin }) => {
  const isCreator = appt._isCreator || isAdmin;
  const isBooked = appt.bookingStatus === 'booked';
  const isQueued = appt.userActionRequest === 'Queue';
  return (
    <tr style={{ backgroundColor: '#f8f9fa' }}>
      <td colSpan="3"></td>
      <td>
        Players: {(appt.playerNames || []).join(', ')}
        {appt.bookingFeedback && <div><i style={{ color: 'blue' }}>{appt.bookingFeedback}</i></div>}
      </td>
      <td>
        {appt.joinSyndicate && <span title="Join Syndicate" aria-label="Join Syndicate">🤝</span>}
      </td>
      <td>{appt.createdBy?.username || 'Unknown'}</td>
      <td>
        {appt.bookingStatus !== 'unclaimed' && (
          <span className={`badge ${appt.bookingStatus === 'booked' ? 'bg-success' : 'bg-warning'}`}>
            {appt.bookingStatus}
          </span>
        )}
      </td>
      <td>
        <button type="button" className="btn btn-sm btn-warning me-1" onClick={() => onEdit(appt._id)}>
          Edit
        </button>
        {isCreator ? (
          <button type="button" className="btn btn-sm btn-danger me-1" onClick={() => onDelete(appt._id)}>
            Delete
          </button>
        ) : (
          <button type="button" className="btn btn-sm btn-outline-danger me-1" onClick={() => onRemoveMe(appt._id)}>
            Remove Me
          </button>
        )}
        <button
          type="button"
          disabled={isBooked}
          className={`btn btn-sm ${isQueued ? 'btn-danger' : 'btn-success'}`}
          onClick={() => isQueued ? onStop(appt._id) : onQueue(appt._id)}
        >
          {isBooked ? 'Booked' : isQueued ? 'Stop' : 'Execute'}
        </button>
      </td>
    </tr>
  );
};

const EventItem = ({ event, deleteAppt, editAppt, queueAppt, stopAppt, removePlayer, getColor, htmlDecode, expanded, onToggle, isAdmin }) => {
  const appointments = event.appointment || [];
  const hasCreatorAppt = appointments.some(a => a._isCreator);
  const hasAnyAppt = appointments.length > 0;
  return (
    <>
      <tr onClick={() => onToggle(event.eventID)} style={{ cursor: 'pointer' }}>
        <td>{event.eventID}</td>
        <td>{event.eventDate.substring(0, 15)}</td>
        <td>{event.eventNickname}</td>
        <td>{htmlDecode(event.desc)}</td>
        <td>{event.timeOfDay}</td>
        <td>{event.gender}</td>
        <td className={`timesheet-open ${getColor(event.tsOpen, hasAnyAppt)}`}>
          {event.tsOpen.substring(0, 24)}
        </td>
        <td colSpan="3">
          <button type="button" className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); editAppt(event.eventID, 'new'); }}>
            New Appointment
          </button>
          {expanded ? ' ▲' : ' ▼'}
        </td>
      </tr>
      {expanded && appointments.map(appt => (
        <AppointmentRow
          key={appt._id}
          appt={appt}
          eventId={event.eventID}
          onEdit={editAppt}
          onDelete={deleteAppt}
          onQueue={queueAppt}
          onStop={stopAppt}
          onRemoveMe={removePlayer}
          isAdmin={isAdmin}
        />
      ))}
    </>
  );
};

const EventsList = forwardRef((props, ref) => {
  const [events, setEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const initialLoadDoneRef = useRef(false);
  const userProfileRef = useRef(null);
  const navigate = useNavigate();

  // Keep ref in sync with state so closures always read the latest value
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  useImperativeHandle(ref, () => ({
    refreshEvents,
  }));

  useEffect(() => {
    auth.fetch('/accounts/profile').then(data => {
      setUserProfile(data);
      userProfileRef.current = data;
      setIsAdmin(data.role === 'admin');
    }).catch(() => {});
  }, []);

  const refreshEvents = async () => {
    try {
      const response = await auth.fetch('/events/', { method: 'GET' });
      const profile = userProfileRef.current;
      const updatedEvents = response.map((ev) => {
        const appointments = (ev.appointment || []).map(appt => ({
          ...appt,
          _isCreator: profile && (
            appt.createdBy?.username === profile.username ||
            appt.createdBy?._id === profile._id
          ),
        }));
        return { ...ev, appointment: appointments };
      });
      setEvents(updatedEvents);
      // Auto-expand on first load only
      if (!initialLoadDoneRef.current) {
        const expanded = {};
        updatedEvents.forEach(ev => {
          if (ev.appointment && ev.appointment.length > 0) {
            expanded[ev.eventID] = true;
          }
        });
        setExpandedEvents(expanded);
        initialLoadDoneRef.current = true;
      }
    } catch (error) {
      console.error('Fetch events failed:', error);
    }
  };

  const htmlDecode = (input) => {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  };

  const getColor = (dateStr, hasAppt) => {
    const openDate = new Date(dateStr);
    const nowDT = new Date();
    const diffMilli = openDate - nowDT;
    const limit1 = 2 * 86400000;
    const limit2 = 7 * 86400000;
    if (hasAppt) return 'bg-baby-blue';
    if (diffMilli <= 0) return 'bg-light-green';
    if (diffMilli > 0 && diffMilli <= limit1) return 'bg-light-red';
    if (diffMilli > limit1 && diffMilli <= limit2) return 'bg-light-yellow';
    return 'bg-transparent';
  };

  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  // Auto-expand events that have appointments, on initial load only
  useEffect(() => {
    if (events.length > 0) {
      const expanded = {};
      events.forEach(ev => {
        if (ev.appointment && ev.appointment.length > 0) {
          expanded[ev.eventID] = true;
        }
      });
      setExpandedEvents(expanded);
    }
  }, []);

  const deleteAppointment = async (apptId) => {
    try {
      await auth.fetch(`/appointments/${apptId}`, { method: 'DELETE' });
      refreshEvents();
    } catch (error) {
      console.error('Delete appointment failed:', error);
    }
  };

  const queueAppointment = async (apptId) => {
    try {
      await auth.fetch(`/appointments/queue/${apptId}`, { method: 'POST' });
      refreshEvents();
    } catch (error) {
      console.error('Queue appointment failed:', error);
    }
  };

  const stopAppointment = async (apptId) => {
    try {
      await auth.fetch(`/appointments/stop/${apptId}`, { method: 'POST' });
      refreshEvents();
    } catch (error) {
      console.error('Stop appointment failed:', error);
    }
  };

  const removePlayer = async (apptId) => {
    try {
      await auth.fetch(`/appointments/${apptId}/remove-player`, { method: 'POST' });
      refreshEvents();
    } catch (error) {
      console.error('Remove player failed:', error);
    }
  };

  const editAppointment = (apptId) => {
    navigate(`/editappointment/${apptId}`);
  };

  useEffect(() => {
    refreshEvents();
    const interval = setInterval(refreshEvents, 5000);
    return () => clearInterval(interval);
  }, []);

  const eventList = events.map((ev) => (
    <EventItem
      key={ev.eventID}
      event={ev}
      deleteAppt={deleteAppointment}
      editAppt={editAppointment}
      queueAppt={queueAppointment}
      stopAppt={stopAppointment}
      removePlayer={removePlayer}
      getColor={getColor}
      htmlDecode={htmlDecode}
      expanded={expandedEvents[ev.eventID]}
      onToggle={toggleExpand}
      isAdmin={isAdmin}
    />
  ));

  return (
    <div>
      <h3>Upcoming MLGC Events</h3>
      <table className="table">
        <thead className="thead-light">
          <tr>
            <th>ID</th>
            <th>Date</th>
            <th>Name</th>
            <th width="25%">Description</th>
            <th>Time</th>
            <th>Gender</th>
            <th>Timesheet Open</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{eventList}</tbody>
      </table>
    </div>
  );
});

export default EventsList;
